/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { type AxiosError } from "axios";
import endpoints, { ApiHost } from "./endpoints";
import { authHeader, getAccessToken, getRefreshToken, nonAuthRouters } from "./authHeader";
import type { Token } from "@/types";
import { useAuthStore, useErrorStore } from "@/stores";
import constants from "./constants";

const ajax = axios.create({
  baseURL: ApiHost,
  withCredentials: true,
});

let refreshPromise: Promise<any> | null = null;

ajax.interceptors.request.use(cfg => {
  const withToken = nonAuthRouters.findIndex(i => cfg.url === i) < 0;
  Object.assign(cfg.headers, authHeader(withToken));
  return cfg;
});

ajax.interceptors.response.use(
  (res) => {
    return res.data
  },
  (error) => {
    return handleError(error)
  }
)

const canGotoLogin = (url: string) => {
  if (!getAccessToken() || !getRefreshToken()) {
    return true;
  } else if (nonAuthRouters.findIndex(i => url.startsWith(i)) >= 0) {
    return true;
  } else {
    return false;
  }
}

const handleError = async (error: AxiosError) => {
  // store.dispatch(changeStatusCode(response.status));
  const { response } = error;
  if (response) {
    if (response.status === 401) {
      const authStore = useAuthStore()
      // 無限ループを回避するため
      if (error.config && error.config.url && canGotoLogin(error.config.url)) {
        // リフレッシュとログアウトでエラー発生したら、再ログインする
        authStore.clearToken()
      } else {
        // リフレッシュとログアウトでエラー発生以外の場合
        if (!refreshPromise) {
          refreshPromise = ajax.post<any, Token>(endpoints.api.auth.refresh, { token: getRefreshToken() }).then(data => {
            authStore.setToken(data)
            return data;
          }).catch(() => {
            // トークン更新失敗時、ログイン画面に戻る
            authStore.clearToken()
          });
        }
        return refreshPromise.then(data => {
          if (error.config && data.access) {
            return ajax.request({ method: error.config.method, url: error.config.url, data: error.config.params });
          } else {
            // 新トークン取得できない場合、再ログインする
            authStore.clearToken()
            return data;
          }
        }).finally(() => {
          refreshPromise = null;
        });
      }
    } else if (response.status === 500 || response.status === 403 || response.status === 404) {
      const errorStore = useErrorStore()
      errorStore.status_code = response.status
      if (response.data) {
        const data: any = response.data
        errorStore.detail = data.detail
        errorStore.endpoint = error.config?.url || ""
      }
    }
    return Promise.reject(response.data);
  } else if (error.code === 'ERR_NETWORK') {
    const errorStore = useErrorStore()
    errorStore.setStatusCode(500, constants.ERROR.NETWORK)
    return Promise.reject({ detail: constants.ERROR.NETWORK, status_code: 500 })
  } else if (error.message) {
    return Promise.reject({ detail: error.message, status_code: 500 })
  }
  return Promise.reject({});
}

export default ajax
